import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getStationeryStockCountById } from 'src/redux/slices/stationery-stock-count';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';
import StationeryStockCountNewEditForm from '../stationery-stock-count-new-edit-form';

type Props = {
  id: string;
};

export default function StationeryStockCountEditView({ id }: Props) {
  const {
    stockCount: currentStockCount,
    status,
    error,
  } = useAppSelector((state) => state.stationeryStockCount);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getStationeryStockCountById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load stock count details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.stationeryStockCount.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Stock Counts
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  if (status === 'success' && currentStockCount) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Edit Stock Count"
          links={[
            {
              name: 'Stock Count',
              href: paths.dashboard.stationeryStockCount.root,
            },
            { name: 'Edit Stock Count' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <StationeryStockCountNewEditForm currentStockCount={currentStockCount} />
      </Container>
    );
  }

  return <span />;
}
